import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";

export default function VerifyEmail() {
  const { t } = useI18n();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const pending = params.get("pending");
  const hasProcessed = useRef(false);

  // status: 'idle' | 'verifying' | 'success' | 'error'
  const [status, setStatus] = useState(token ? "verifying" : "idle");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token || hasProcessed.current) return;
    hasProcessed.current = true;
    (async () => {
      try {
        await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
        setStatus("success");
        await refresh();
      } catch {
        setStatus("error");
      }
    })();
  }, [token, refresh]);

  const resend = async () => {
    if (!user?.email) {
      toast.error("Email manquant");
      return;
    }
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email: user.email });
      toast.success(t("verify.resent"));
    } catch {
      toast.error("Erreur");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-12">
          <span className="block w-2.5 h-2.5 bg-white" />
          <span className="font-display text-xl">Versant<span className="text-zinc-500">.</span>Studio</span>
        </Link>

        <div className="border border-white/10 p-8 sm:p-10 bg-zinc-900/30">
          {status === "verifying" && (
            <>
              <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">…</p>
              <h1 className="font-display text-3xl tracking-tighter mb-3">{t("common.loading")}</h1>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle2 size={36} strokeWidth={1.4} className="text-emerald-400 mb-6" />
              <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">·</p>
              <h1 className="font-display text-3xl tracking-tighter mb-3">{t("verify.success")}</h1>
              <button
                onClick={() => navigate("/dashboard")}
                data-testid="verify-continue"
                className="mt-6 inline-flex items-center gap-2 bg-white text-zinc-950 hover:bg-zinc-200 px-6 py-3 text-sm font-medium"
              >
                {t("verify.continue")} <ArrowRight size={14} />
              </button>
            </>
          )}

          {status === "error" && (
            <>
              <AlertCircle size={36} strokeWidth={1.4} className="text-rose-400 mb-6" />
              <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">·</p>
              <h1 className="font-display text-3xl tracking-tighter mb-3">{t("verify.failed")}</h1>
              {user && !user.email_verified && (
                <button
                  onClick={resend}
                  disabled={resending}
                  data-testid="verify-resend"
                  className="mt-6 inline-flex items-center gap-2 border border-white/20 hover:border-white/60 px-6 py-3 text-sm disabled:opacity-50"
                >
                  <Mail size={14} />
                  {resending ? t("common.loading") : t("verify.resend")}
                </button>
              )}
            </>
          )}

          {status === "idle" && (
            <>
              <Mail size={36} strokeWidth={1.4} className="text-zinc-300 mb-6" />
              <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">·</p>
              <h1 className="font-display text-3xl tracking-tighter mb-3">{t("verify.title")}</h1>
              <p className="text-sm text-zinc-400 leading-relaxed mb-2">{t("verify.intro")}</p>
              {user?.email && (
                <p className="text-sm text-zinc-200 font-mono mb-6">{user.email}</p>
              )}
              <div className="flex flex-wrap gap-3">
                {user && (
                  <button
                    onClick={resend}
                    disabled={resending}
                    data-testid="verify-resend"
                    className="inline-flex items-center gap-2 bg-white text-zinc-950 hover:bg-zinc-200 px-6 py-3 text-sm font-medium disabled:opacity-50"
                  >
                    <Mail size={14} />
                    {resending ? t("common.loading") : t("verify.resend")}
                  </button>
                )}
                <button
                  onClick={() => navigate("/dashboard")}
                  className="inline-flex items-center gap-2 border border-white/20 hover:border-white/60 px-6 py-3 text-sm"
                >
                  {t("verify.continue")} <ArrowRight size={14} />
                </button>
              </div>
              {pending && (
                <p className="mt-6 text-xs text-zinc-500">
                  Vérifiez votre boîte mail (et les spams).
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
